import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Text } from '@billda/design-system';
import { useInvitationFlowStore } from '../stores/invitationFlowStore';
import { getInvitationInfo } from '../api/invitationApi';

/**
 * Entry point for invitation flow
 * Fetches invitation info and redirects to appropriate step
 */
const InvitationLoadingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Using shallow access for individual properties
  const setInvitationInfo = useInvitationFlowStore(
    state => state.setInvitationInfo
  );
  const setInvitationId = useInvitationFlowStore(
    state => state.setInvitationId
  );

  useEffect(() => {
    // Handle missing ID
    if (!id) {
      navigate('/invitation/error', { replace: true });
      return;
    }

    // Fetch invitation info
    const fetchInvitation = async () => {
      try {
        const response = await getInvitationInfo(id);

        // Store in Zustand
        setInvitationInfo(response);
        setInvitationId(id);

        // Navigate based on requirements
        const { requiresProfileCapture, requiresDataSharingConsent } = response;

        if (requiresProfileCapture) {
          navigate(`/invitation/${id}/profile`, { replace: true });
        } else if (requiresDataSharingConsent) {
          navigate(`/invitation/${id}/consent`, { replace: true });
        } else {
          navigate(`/invitation/${id}/processing`, { replace: true });
        }
      } catch (error) {
        console.error('Failed to load invitation:', error);
        navigate(`/invitation/${id}/error`, { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [id, navigate, setInvitationInfo, setInvitationId]);

  // Show loading state only when actually loading
  if (!loading) {
    return null; // Component will navigate away, no need to show anything
  }

  return (
    <div className="mx-auto w-full max-w-[600px] px-4">
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6">
        <Spinner size={60} />
        <Text variant="h6" color="secondary">
          Loading invitation...
        </Text>
        <Text
          as="div"
          variant="body2"
          color="secondary"
          className="text-center"
        >
          Please wait while we verify your invitation
        </Text>
      </div>
    </div>
  );
};

export default InvitationLoadingPage;
